import { CASH_CROP_HARVEST_TYPES, RDCashCropInput, RDCashCropSpecies } from '@cibo/core'
import { AutocompleteField, CheckboxField, DatePickerField, NumberInputField } from '@cibo/ui'
import { Collapse, FormHelperText, Stack, StackProps } from '@mui/material'
import InputLabel from '@mui/material/InputLabel'
import { useFormikContext } from 'formik'
import { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailEditorPropsByYear } from '../types'
import { useIsRequired } from '../useIsRequired'
import { useShowDetailAttribute } from '../useShowDetailAttribute'
import {
  CashCropEditorInputs,
  perAcreUnitKeyByHarvest,
  SCOPED_HARVEST_TYPE_OPTIONS,
  SCOPED_PRIORITY_HARVEST_TYPES,
} from './types'

const ConditionallyShow = ({ show, children }: { show: boolean; children: ReactNode }) =>
  show ? <>{children}</> : <></>

const findDefaultHarvestMonth = (year: number, crop: string) =>
  new Date(year, crop === 'corn' ? 7 : 8)

const dateBasedOnValue = (dateInput: Date | undefined | string, year: number) =>
  dateInput && dateInput !== '' ? new Date(dateInput) : new Date(year, 0, 1)

export const CashCropHarvestInputs = (props: DetailEditorPropsByYear) => {
  const { filterInputs, name, requirement, year } = props
  const { t } = useTranslation('@cibo/landmanager/CashCropEditor')

  const { setFieldValue, getFieldMeta, initialValues } = useFormikContext<CashCropEditorInputs>()

  const meta = getFieldMeta<CashCropEditorInputs>(name as string)
  const { crop, plantedAt, harvestedAt, harvestType, cropFailure, lossAt, lossPercentage, source } =
    meta.value || {}

  // @ts-ignore formik doesn't correctly type the subfields
  const metaError: Record<keyof CashCropEditorInputs, string | undefined> = meta.error || {}
  const lossPercentageError = metaError.lossPercentage
  const isCiboSourced = (subField: keyof RDCashCropInput) =>
    !!meta.initialValue?.[subField] &&
    source === 'cibo' &&
    meta.initialValue?.[subField] === meta.value[subField]

  const benchmarkRequires = useIsRequired(requirement)
  const show = useShowDetailAttribute<CashCropEditorInputs>(filterInputs)
  const defaultHarvestMonth = findDefaultHarvestMonth(year, crop)

  // NOTE: the presence of various <Collapse> components makes spacing via Stack unprecictable,
  // so please use this constant for paddingBottom instead of adding Stack spacing
  const spacingValue = 1

  const harvestVisible = () => {
    if (!cropFailure) {
      return true
    } else if (lossPercentage && lossPercentage < 100) {
      return true
    } else {
      return false
    }
  }

  useEffect(() => {
    if (
      !cropFailure &&
      (lossAt !== initialValues?.lossAt || lossPercentage !== initialValues?.lossPercentage)
    ) {
      setFieldValue(`${name}.lossPercentage`, undefined)
      setFieldValue(`${name}.lossAt`, undefined)
    }
  }, [cropFailure])

  return (
    <Stack>
      <ConditionallyShow show={show('harvestedAt')}>
        <Stack paddingBottom={spacingValue}>
          <InputLabel required={!!benchmarkRequires('harvestedAt')} htmlFor={`${name}.harvestedAt`}>
            {t('harvested')}
          </InputLabel>
          <DatePickerField
            name={`${name}.harvestedAt`}
            inlineClearButton
            ciboSourced={isCiboSourced('harvestedAt')}
            // @ts-ignore plantedAt is cast to string in MUI DatePickerField
            minDate={dateBasedOnValue(plantedAt, year)}
            maxDate={new Date(year + 1, 6, 1)}
            referenceDate={defaultHarvestMonth}
            data-testid={`cashCrop.${year}.harvestedAt`}
            stringValue
          />
        </Stack>
      </ConditionallyShow>
      <CheckboxInput {...props} inputName="cropFailure" />
      <Collapse in={cropFailure === true}>
        <Stack flex={1} paddingBottom={spacingValue}>
          <InputLabel required={!!benchmarkRequires('lossAt')} htmlFor={`${name}.lossAt`}>
            {t('lossAt')}
          </InputLabel>
          <DatePickerField
            name={`${name}.lossAt`}
            inlineClearButton
            error={!!metaError.lossAt}
            // @ts-ignore plantedAt is cast to string in MUI DatePickerField
            minDate={plantedAt ? dateBasedOnValue(plantedAt, year) : new Date(year, 0, 1)}
            // @ts-ignore harvestedAt is cast to string in MUI DatePickerField
            maxDate={harvestedAt ? dateBasedOnValue(harvestedAt, year) : new Date(year + 1, 6, 1)}
            referenceDate={defaultHarvestMonth}
            data-testid={`cashCrop.${year}.lossAt`}
            slotProps={{ textField: { error: !!metaError.lossAt } }}
            stringValue
          />
        </Stack>
        <Stack flex={1} paddingBottom={spacingValue}>
          <InputLabel
            required={!!benchmarkRequires('lossPercentage')}
            htmlFor={`${name}.lossPercentage`}
          >
            {t('lossPercentage')}
          </InputLabel>

          <NumberInputField
            name={`${name}.lossPercentage`}
            min={0}
            max={100}
            unit="%"
            data-testid={`cashCrop.${year}.lossPercentage`}
          />
          {!!lossPercentageError && <FormHelperText error>{lossPercentageError}</FormHelperText>}
        </Stack>
      </Collapse>
      {/* TODO: make it so this is less of a bouncy experience when going from 100 to empty */}
      <Collapse in={harvestVisible()}>
        <ConditionallyShow show={show('harvestType')}>
          <Stack paddingBottom={spacingValue}>
            <InputLabel
              required={
                // if yield per acre is required, harvest type is de facto required
                !!benchmarkRequires('harvestType') || !!benchmarkRequires('yieldPerAcre')
              }
              htmlFor={`${name}.harvestType`}
            >
              {t('harvestType')}
            </InputLabel>
            <AutocompleteField
              name={`${name}.harvestType`}
              options={
                (crop && SCOPED_HARVEST_TYPE_OPTIONS[crop as RDCashCropSpecies]) ||
                CASH_CROP_HARVEST_TYPES
              }
              groupBy={
                crop
                  ? c =>
                      SCOPED_PRIORITY_HARVEST_TYPES[crop as RDCashCropSpecies] &&
                      SCOPED_PRIORITY_HARVEST_TYPES[crop as RDCashCropSpecies]?.includes(c)
                        ? t('suggested')
                        : t('all')
                  : undefined
              }
              getOptionLabel={(option: string) => t(option)}
              data-testid={`cashCrop.${year}.harvestType`}
              selectOnFocus
            />
          </Stack>
        </ConditionallyShow>
        <Collapse
          in={
            (show('yieldPerAcre') && !!benchmarkRequires('yieldPerAcre')) ||
            (!!harvestType && !['none', 'grazed', 'not_available'].find(a => a === harvestType))
          }
        >
          <Stack paddingBottom={spacingValue}>
            <InputLabel
              required={!!benchmarkRequires('yieldPerAcre')}
              htmlFor={`${name}.yieldPerAcre`}
            >
              {t('whatYield')}
            </InputLabel>

            <NumberInputField
              disabled={!harvestType}
              name={`${name}.yieldPerAcre`}
              min={0}
              unit={t(perAcreUnitKeyByHarvest(harvestType))}
              data-testid={`cashCrop.${year}.yieldPerAcre`}
            />
            {!harvestType && (
              <FormHelperText error={!!benchmarkRequires('yieldPerAcre')}>
                {t('requiresType')}
              </FormHelperText>
            )}
          </Stack>
        </Collapse>
        <ConditionallyShow show={show('residuePercentageRemoved')}>
          <Stack paddingBottom={spacingValue}>
            <InputLabel
              required={!!benchmarkRequires('residuePercentageRemoved')}
              htmlFor={`${name}.residuePercentageRemoved`}
            >
              {t('removedPercentage')}
            </InputLabel>
            <NumberInputField
              name={`${name}.residuePercentageRemoved`}
              min={0}
              max={100}
              unit={'%'}
              data-testid={`cashCrop.${year}.residuePercentageRemoved`}
            />
            <FormHelperText>{t('removedPercentageHelper')}</FormHelperText>
          </Stack>
        </ConditionallyShow>
        <CheckboxInput {...props} inputName="herbicideBurndown" />
        <CheckboxInput {...props} inputName="fireBurndown" />
      </Collapse>
    </Stack>
  )
}

const CheckboxInput = ({
  name,
  requirement,
  filterInputs,
  inputName,
  year,
  stackProps,
}: DetailEditorPropsByYear & {
  inputName: keyof CashCropEditorInputs
  stackProps?: StackProps
}) => {
  const { t } = useTranslation('@cibo/landmanager/CashCropEditor')
  const benchmarkRequires = useIsRequired(requirement)
  const show = useShowDetailAttribute<CashCropEditorInputs>(filterInputs)
  return (
    <ConditionallyShow show={show(inputName)}>
      <Stack direction="row" alignItems="center" {...stackProps}>
        <CheckboxField
          name={`${name}.${inputName}`}
          data-testid={`cashCrop.${year}.${inputName}`}
        />
        <InputLabel required={!!benchmarkRequires(inputName)} htmlFor={`${name}.${inputName}`}>
          {t(inputName)}
        </InputLabel>
      </Stack>
    </ConditionallyShow>
  )
}
